import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Slider,
  Grid,
  Divider,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import type { UpdatePricingFormData } from '../types/subscription.types';
import { useUpdatePricing, useCurrentPricing } from '../hooks/useFleetSubscriptions';
import {
  formatNaira,
  calculateRatesFromDiscount,
  calculateDiscounts,
} from '../../../services/fleetSubscriptionService';

interface UpdatePricingDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const validationSchema = yup.object({
  monthly_rate: yup
    .number()
    .required('Monthly rate is required')
    .min(1000, 'Monthly rate must be at least ₦1,000')
    .max(1000000, 'Monthly rate cannot exceed ₦1,000,000'),
  quarterly_rate: yup
    .number()
    .required('Quarterly rate is required')
    .min(1000, 'Quarterly rate must be at least ₦1,000'),
  yearly_rate: yup
    .number()
    .required('Yearly rate is required')
    .min(1000, 'Yearly rate must be at least ₦1,000'),
  discount_percentage: yup.number().min(0).max(50),
  notes: yup
    .string()
    .required('Change notes are required')
    .min(10, 'Notes must be at least 10 characters'),
});

const UpdatePricingDialog: React.FC<UpdatePricingDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { data: currentPricing } = useCurrentPricing();
  const updatePricing = useUpdatePricing();
  const [useAutoCalculate, setUseAutoCalculate] = useState(true);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UpdatePricingFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      monthly_rate: currentPricing?.monthly_rate || 50000,
      quarterly_rate: currentPricing?.quarterly_rate || 135000,
      yearly_rate: currentPricing?.yearly_rate || 540000,
      discount_percentage: currentPricing
        ? calculateDiscounts(currentPricing).quarterly_discount_percent
        : 10,
      notes: '',
    },
  });

  const monthlyRate = watch('monthly_rate');
  const quarterlyRate = watch('quarterly_rate');
  const yearlyRate = watch('yearly_rate');
  const discountPercentage = watch('discount_percentage');

  // Auto-calculate quarterly and yearly rates based on discount percentage
  useEffect(() => {
    if (useAutoCalculate && monthlyRate) {
      const calculated = calculateRatesFromDiscount(monthlyRate, discountPercentage);
      setValue('quarterly_rate', calculated.quarterly_rate);
      setValue('yearly_rate', calculated.yearly_rate);
    }
  }, [monthlyRate, discountPercentage, useAutoCalculate, setValue]);

  useEffect(() => {
    if (open && currentPricing) {
      const currentDiscounts = calculateDiscounts(currentPricing);
      reset({
        monthly_rate: currentPricing.monthly_rate,
        quarterly_rate: currentPricing.quarterly_rate,
        yearly_rate: currentPricing.yearly_rate,
        discount_percentage: currentDiscounts.quarterly_discount_percent,
        notes: '',
      });
    }
  }, [open, currentPricing, reset]);

  const onSubmit = async (data: UpdatePricingFormData) => {
    try {
      await updatePricing.mutateAsync({
        monthly_rate: data.monthly_rate,
        quarterly_rate: data.quarterly_rate,
        yearly_rate: data.yearly_rate,
        notes: data.notes,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error('Update pricing error:', error);
    }
  };

  const handleClose = () => {
    if (!updatePricing.isPending) {
      onClose();
    }
  };

  // Calculate actual discount percentages
  const actualQuarterlyDiscount =
    monthlyRate > 0
      ? ((monthlyRate * 3 - quarterlyRate) / (monthlyRate * 3)) * 100
      : 0;
  const actualYearlyDiscount =
    monthlyRate > 0
      ? ((monthlyRate * 12 - yearlyRate) / (monthlyRate * 12)) * 100
      : 0;

  const quarterlySavings = monthlyRate * 3 - quarterlyRate;
  const yearlySavings = monthlyRate * 12 - yearlyRate;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon sx={{ color: '#f59e0b' }} />
          <Typography variant="h6">Update Subscription Pricing</Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {/* Current Pricing Display */}
          {currentPricing && (
            <Paper
              elevation={0}
              sx={{ p: 2, mb: 3, backgroundColor: 'action.hover', borderRadius: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Current Pricing (Active)
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography variant="body2">
                  Monthly: <strong>{formatNaira(currentPricing.monthly_rate)}</strong>
                </Typography>
                <Typography variant="body2">
                  Quarterly: <strong>{formatNaira(currentPricing.quarterly_rate)}</strong>
                </Typography>
                <Typography variant="body2">
                  Yearly: <strong>{formatNaira(currentPricing.yearly_rate)}</strong>
                </Typography>
              </Box>
            </Paper>
          )}

          {/* New Pricing */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            New Pricing
          </Typography>

          <Grid container spacing={3}>
            {/* Monthly Rate */}
            <Grid item xs={12}>
              <Controller
                name="monthly_rate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Monthly Rate"
                    type="number"
                    fullWidth
                    required
                    error={!!errors.monthly_rate}
                    helperText={errors.monthly_rate?.message}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>

            {/* Discount Slider */}
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Discount Percentage: {Math.round(discountPercentage)}%
              </Typography>
              <Controller
                name="discount_percentage"
                control={control}
                render={({ field }) => (
                  <Slider
                    {...field}
                    min={0}
                    max={50}
                    step={0.5}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 10, label: '10%' },
                      { value: 20, label: '20%' },
                      { value: 30, label: '30%' },
                      { value: 40, label: '40%' },
                      { value: 50, label: '50%' },
                    ]}
                    onChange={(_, value) => {
                      field.onChange(value);
                      setUseAutoCalculate(true);
                    }}
                  />
                )}
              />
              <Typography variant="caption" color="text.secondary">
                Adjust the slider to automatically calculate discounted rates
              </Typography>
            </Grid>

            {/* Quarterly Rate */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="quarterly_rate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Quarterly Rate (3 Months)"
                    type="number"
                    fullWidth
                    required
                    error={!!errors.quarterly_rate}
                    helperText={
                      errors.quarterly_rate?.message ||
                      `${Math.round(actualQuarterlyDiscount)}% discount • Save ${formatNaira(
                        quarterlySavings
                      )}`
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      setUseAutoCalculate(false);
                    }}
                  />
                )}
              />
            </Grid>

            {/* Yearly Rate */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="yearly_rate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Yearly Rate (12 Months)"
                    type="number"
                    fullWidth
                    required
                    error={!!errors.yearly_rate}
                    helperText={
                      errors.yearly_rate?.message ||
                      `${Math.round(actualYearlyDiscount)}% discount • Save ${formatNaira(
                        yearlySavings
                      )}`
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      setUseAutoCalculate(false);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Preview */}
          <Paper
            elevation={0}
            sx={{ p: 2, mb: 3, backgroundColor: 'primary.50', border: '1px dashed', borderColor: 'primary.main' }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Pricing Preview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Monthly
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatNaira(monthlyRate)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Quarterly ({Math.round(actualQuarterlyDiscount)}% off)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {formatNaira(quarterlyRate)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Yearly ({Math.round(actualYearlyDiscount)}% off)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#9333ea' }}>
                  {formatNaira(yearlyRate)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Change Notes */}
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Change Notes *"
                multiline
                rows={3}
                fullWidth
                required
                placeholder="e.g., Price increase due to inflation, Promotional discount for Q1, etc."
                error={!!errors.notes}
                helperText={errors.notes?.message || 'Explain the reason for this pricing change'}
              />
            )}
          />

          {/* Warning Alert */}
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              This will only affect <strong>new subscriptions</strong>. Existing active subscriptions
              will keep their original pricing until renewal.
            </Typography>
          </Alert>

          {/* Error Alert */}
          {updatePricing.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updatePricing.error?.message || 'Failed to update pricing'}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={updatePricing.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updatePricing.isPending}
            startIcon={updatePricing.isPending ? <CircularProgress size={20} /> : <MoneyIcon />}
          >
            {updatePricing.isPending ? 'Updating...' : 'Update Pricing'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdatePricingDialog;
